import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  DataTable,
  Field,
  PageHead,
  Panel,
  PillGroup,
  SelectX,
  StatusPill,
  useToast,
  type Column,
} from "@guestpilot/ui-kit";
import { Document16Regular } from "@fluentui/react-icons";
import { api } from "../api/client";
import { useGet, useWrite } from "../api/hooks";
import type { ExportJob, Schema, Team } from "../api/types";
import { useHandleApiError } from "../errors";
import { formatBytes, formatDateTime } from "../format";
import { useI18n } from "../i18n";
import { useSession } from "../session";

type Create = Schema<"ExportCreate">;
type Campaign = Schema<"Campaign">;
type Scope = Create["scope"];

/** Evidence from GuestPilot's own reviewed data, not a live Graph call: who generated it, when and how big matter as an audit trail (BR-120…BR-125, BR-225). */
export function Exports() {
  const { t, locale } = useI18n();
  const s = useSession();
  const toast = useToast();
  const fail = useHandleApiError();
  const [params] = useSearchParams();
  const tz = s.me.tenant.timeZone;
  const tenantWide = s.scope("EXPORT_CREATE") === "all";
  const teams = useGet<Team[]>("/api/v1/teams", { filter: "all" });
  const campaigns = useGet<Campaign[]>("/api/v1/campaigns", undefined, { enabled: s.can("CAMPAIGN_READ") });
  const [scope, setScope] = useState<Scope>(params.get("team") ? "team" : tenantWide ? "all" : "team");
  const [teamId, setTeamId] = useState(params.get("team") ?? "");
  const [campaignId, setCampaignId] = useState("");
  const [format, setFormat] = useState<string>("xlsx");
  const history = useGet<ExportJob[]>("/api/v1/exports", undefined, { refetchInterval: 4000 });
  const create = useWrite<ExportJob, Create>("POST", "/api/v1/exports", ["/api/v1/exports"]);
  const retry = useWrite<ExportJob>("POST", "/api/v1/exports/:id/retry", ["/api/v1/exports"]);
  useEffect(() => {
    if (!teamId && (teams.data ?? []).length > 0 && scope === "team") setTeamId(teams.data![0]!.id);
  }, [teams.data, scope, teamId]);

  const scopes: { value: Scope; label: string }[] = [
    ...(tenantWide
      ? [
          { value: "all" as const, label: t("ui:exports.scope.all") },
          { value: "domains" as const, label: t("ui:exports.scope.domains") },
          { value: "complete" as const, label: t("ui:exports.scope.complete") },
        ]
      : []),
    { value: "team", label: t("ui:exports.scope.team") },
    ...(s.can("CAMPAIGN_READ")
      ? [{ value: "campaign" as const, label: t("ui:exports.scope.campaign") }]
      : []),
  ];
  const formats =
    scope === "complete" ? ["zip"] : scope === "domains" ? ["csv", "xlsx"] : ["csv", "xlsx", "pdf"];
  const fmt = formats.includes(format) ? format : formats.includes("xlsx") ? "xlsx" : "zip";
  const body: Create = {
    scope,
    format: fmt as Create["format"],
    language: locale,
    ...(scope === "team" ? { teamId } : {}),
    ...(scope === "campaign" ? { campaignId } : {}),
  };
  const missing =
    scope === "team" && !teamId
      ? t("ui:exports.pickTeam")
      : scope === "campaign" && !campaignId
        ? t("ui:exports.pickCampaign")
        : undefined;

  const download = async (j: ExportJob) => {
    try {
      const link = await api.post<Schema<"SignedLink">>(`/api/v1/exports/${j.id}/download-link`);
      if (link.url) window.open(link.url, "_blank", "noopener");
    } catch (e) {
      fail(e);
    }
  };

  const columns: Column<ExportJob>[] = [
    {
      key: "name",
      header: t("ui:exports.col.file"),
      render: (j) => (
        <span className="gp-row">
          <Document16Regular aria-hidden />
          <span className="t-body-strong t-mono">{j.fileName}</span>
        </span>
      ),
    },
    {
      key: "scope",
      header: t("ui:exports.col.scope"),
      render: (j) => (j.team ? j.team.displayName : t(`ui:exports.scope.${j.scope}`)),
    },
    { key: "format", header: t("ui:exports.col.format"), render: (j) => (j.format ?? "").toUpperCase() },
    { key: "by", header: t("ui:exports.col.by"), render: (j) => j.requestedBy?.displayName ?? "—" },
    { key: "at", header: t("ui:exports.col.date"), render: (j) => formatDateTime(j.requestedAt, locale, tz) },
    {
      key: "size",
      header: t("ui:exports.col.size"),
      numeric: true,
      render: (j) => formatBytes(j.sizeBytes, locale),
    },
    {
      key: "status",
      header: t("ui:users.col.status"),
      render: (j) => (
        <StatusPill domain="export" value={j.status ?? "queued"} label={t(`ui:exportStatus.${j.status}`)} />
      ),
    },
    {
      key: "act",
      header: t("ui:table.actions"),
      render: (j) =>
        j.status === "ready" ? (
          <Button variant="ghost" onClick={() => void download(j)}>
            {t("ui:exports.download")}
          </Button>
        ) : j.status === "failed" ? (
          <Button
            variant="ghost"
            onClick={() => retry.mutate({ params: { id: j.id ?? "" } }, { onError: fail })}
          >
            {t("common:retry")}
          </Button>
        ) : null,
    },
  ];

  return (
    <div className="gp-page">
      <PageHead title={t("ui:exports.title")} subtitle={t("ui:exports.lead")} />
      <Card title={t("ui:exports.generate")}>
        <div className="gp-grid-3">
          <Field label={t("ui:exports.col.scope")}>
            <SelectX value={scope} options={scopes} onChange={(e) => setScope(e.target.value as Scope)} />
          </Field>
          {scope === "team" ? (
            <Field label={t("ui:users.col.team")}>
              <SelectX
                value={teamId}
                options={(teams.data ?? []).map((x) => ({ value: x.id, label: x.displayName }))}
                onChange={(e) => setTeamId(e.target.value)}
              />
            </Field>
          ) : null}
          {scope === "campaign" ? (
            <Field label={t("ui:exports.scope.campaign")}>
              <SelectX
                value={campaignId}
                options={[
                  { value: "", label: "—" },
                  ...(campaigns.data ?? []).map((x) => ({ value: x.id ?? "", label: x.name ?? "" })),
                ]}
                onChange={(e) => setCampaignId(e.target.value)}
              />
            </Field>
          ) : null}
        </div>
        <PillGroup
          legend={t("ui:exports.col.format")}
          name="format"
          value={fmt}
          onChange={setFormat}
          options={formats.map((f) => ({
            value: f,
            label: f === "pdf" ? t("ui:exports.pdf") : f.toUpperCase(),
          }))}
        />
        <div className="gp-actions">
          <Button
            variant="primary"
            disabledReason={s.gate("EXPORT_CREATE") ?? missing}
            onClick={() =>
              create.mutate(
                { body },
                { onSuccess: () => toast.success(t("ui:exports.queued")), onError: fail },
              )
            }
          >
            {t("ui:exports.generateAction")}
          </Button>
        </div>
      </Card>
      <section className="gp-stack" aria-labelledby="eh-h">
        <h2 id="eh-h" className="t-title-sm" style={{ margin: 0 }}>
          {t("ui:exports.history")}
        </h2>
        <Panel label={t("ui:exports.history")}>
          <DataTable
            caption={t("ui:exports.history")}
            columns={columns}
            rows={history.data}
            loading={history.isLoading}
            error={history.isError}
            onRetry={() => void history.refetch()}
            errorTitle={t("ui:table.error")}
            retryLabel={t("common:retry")}
            rowKey={(j) => j.id ?? ""}
            emptyTitle={t("ui:exports.empty")}
          />
        </Panel>
      </section>
    </div>
  );
}
